import React, { useState, useEffect } from 'react';
import { UsersIcon, ChevronDownIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

/**
 * BandSelector component for selecting which band a sheet/setlist belongs to
 * 
 * @param {Object} props - Component props
 * @param {string} props.selectedBandId - Currently selected band ID
 * @param {Function} props.onBandChange - Callback when band selection changes
 * @param {string} props.placeholder - Placeholder text
 */
const BandSelector = ({ selectedBandId, onBandChange, placeholder = "Select Band" }) => {
  const [userBands, setUserBands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const { isAuthenticated } = useAuth();

  // Fetch user's bands
  useEffect(() => {
    if (!isAuthenticated) return;
    
    const fetchUserBands = async () => {
      setLoading(true);
      try {
        const response = await fetch('/api/bands?category=my', {
          credentials: 'include'
        });
        
        if (response.ok) {
          const result = await response.json();
          setUserBands(result.data || []);
        } else {
          console.error('Failed to fetch user bands');
        }
      } catch (error) {
        console.error('Error fetching user bands:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserBands();
  }, [isAuthenticated]);

  if (!isAuthenticated) {
    return null; // Don't show band selector for unauthenticated users
  }

  const selectedBand = userBands.find(band => band._id === selectedBandId);

  const handleBandSelect = (bandId) => {
    onBandChange(bandId);
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none min-w-[140px] justify-between"
        disabled={loading}
      >
        <div className="flex items-center gap-2">
          <UsersIcon className="w-4 h-4 text-gray-500" />
          <span className={selectedBand ? 'text-gray-900' : 'text-gray-500'}>
            {loading ? 'Loading...' : selectedBand ? selectedBand.name : placeholder}
          </span>
        </div>
        <ChevronDownIcon className="w-4 h-4 text-gray-400" />
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />
          
          {/* Dropdown */}
          <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-200 rounded shadow-lg z-20 max-h-48 overflow-auto">
            {/* Personal option */}
            <button
              type="button"
              onClick={() => handleBandSelect(null)}
              className={`w-full text-left px-3 py-2 hover:bg-gray-100 flex items-center gap-2 ${
                !selectedBandId ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
              }`}
            >
              <div className="w-4 h-4 rounded bg-gray-300" />
              <span className="text-sm">Personal</span>
            </button>
            
            {/* Band options */}
            {userBands.map(band => (
              <button
                key={band._id}
                type="button"
                onClick={() => handleBandSelect(band._id)}
                className={`w-full text-left px-3 py-2 hover:bg-gray-100 flex items-center gap-2 ${
                  selectedBandId === band._id ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                }`}
              >
                <UsersIcon className="w-4 h-4 text-gray-500" />
                <span className="text-sm truncate">{band.name}</span>
                {band.user_role === 'admin' && (
                  <span className="text-xs bg-red-100 text-red-700 px-1 rounded">Admin</span>
                )}
              </button>
            ))}
            
            {userBands.length === 0 && !loading && (
              <div className="px-3 py-2 text-sm text-gray-500">
                No bands available
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default BandSelector;